// record-card.js — the shareable record card (Brief 021).
//
// The card is the artifact a convinced visitor spreads: two Mandarin sentences
// they just understood, the four atoms those sentences are built from, the line
// "I understood these. I have never studied Mandarin Chinese.", and the
// SpeechGenie mark. Design of record: the record pane in
// `~/Developer/Kickstarter assets/SHARE-MODAL-l2b-v3.html`, re-laid out from
// v3's 1200×675 to a 1080×1080 square (Brief 021 Step 1 — square survives every
// share surface; a 16:9 gets letterboxed or centre-cropped on half of them).
//
// NO COUNT, NO TIME CLAIM (policy, Brief 021). v3's "two minutes" watermark is
// dropped, and "I understood these" is deliberately not "I understood 6" — a
// claim a reader tests by looking, not one they compare.
//
// NO SERVER. The card is FIXED — the same six for everyone — so there is no
// Worker, no OG-image endpoint, and nothing to keep in sync with the corpus
// beyond the tile URLs the reveal has already resolved.
//
// ── WHY SVG *AND* CANVAS, AND WHERE THE LINE IS ────────────────────────────
//
// The brief asks for the card as off-screen SVG rasterised through a canvas.
// One thing has to be built differently from the way it reads, and it matters:
//
//   An SVG loaded into an `Image` renders in the browsers' *secure static mode*
//   — no scripts, no external resource loading. An `<image href="https://cdn…">`
//   inside that SVG SIMPLY NEVER LOADS, and no `crossorigin` attribute changes
//   that, because the fetch never happens at all. A card built that way
//   rasterises with six empty cells and no error anywhere.
//
// So the two halves are composited rather than nested: the SVG carries the
// background, the white image cells and ALL the typography (that is what the
// CJK-safe stack is for, and text-as-`<text>` is what makes the glyphs
// rasterise), and the six line drawings are drawn straight onto the canvas
// afterwards, into the rects the SVG reserved. `buildCard()` returns both the
// markup and those rects from ONE layout pass, so they cannot drift.
//
// That is also where `crossorigin="anonymous"` earns its place, exactly as the
// brief has it: the tile `Image`s are the only cross-origin pixels that reach
// the canvas, and without the attribute the canvas taints and `toBlob()` throws
// SecurityError. `cdn.speechgenie.online` answers `access-control-allow-origin:
// *` (re-confirmed 2026-09-10 against `l2b/images/TheDoor.png`), so the CORS
// request succeeds and the canvas stays clean. The SVG half is now pure vector
// + text with no external references, which is the one form of SVG that has
// never tainted a canvas in any engine.

/** The card is square: 1080×1080 (Brief 021 Step 1). */
export const CARD_SIZE = 1080;

/**
 * The card's palette is HARDCODED BRAND, not the running theme's tokens.
 * The demo can be served on the dojo skin or the SpeechGenie-blue embed skin,
 * but the card is one artifact with one look wherever it is generated — a
 * shared image that changed colour with the visitor's build would be a
 * different card, not a themed one. `--sg-blue` (theme-embed.css) is the one
 * SpeechGenie constant; repoint both together if it ever moves.
 */
export const CARD_COLORS = {
  bg: "#153e63",
  fg: "#ffffff",
  muted: "#b9c9d8",
  rule: "rgba(255,255,255,0.22)",
  /** The line drawings are transparent PNGs made for white paper. */
  cell: "#ffffff",
};

/**
 * The CJK-safe stack, as CSS. Every entry is a face already present on the
 * target OS — Apple (PingFang SC), Windows (Microsoft YaHei), Android/Linux
 * (Noto Sans CJK SC) — because NO WEBFONT CAN BE USED HERE: an SVG rasterised
 * through an `Image` cannot reach the parent document's fonts, and bundling a
 * 55 MB Unicode face is exactly what tokens.css exists to avoid. Latin faces
 * lead so the English lines look right; per-glyph fallback picks the CJK face
 * up for the Chinese.
 */
export const CARD_FONT =
  '-apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Roboto, ' +
  '"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans CJK SC", ' +
  "Arial, sans-serif";

/**
 * The layout, in card pixels, as data. v3's proportions re-cut for the square:
 * the sentences lead (a card is seen all at once, and the PAIR is what proves
 * recombination), the atoms sit under a hairline, the claim and the mark are
 * pinned to the foot.
 */
const L = {
  padX: 72,
  padTop: 100,
  padBottom: 68,
  /** Sentences: image cell, then the Mandarin. */
  sentCell: 136,
  sentRadius: 18,
  sentGap: 34,
  sentRowGap: 30,
  sentFont: 100,
  /** The hairline between the sentences and their parts. */
  ruleAbove: 54,
  ruleBelow: 48,
  labelFont: 26,
  labelBelow: 24,
  /** Atoms: four columns, cell above the word. */
  atomGap: 24,
  atomCell: 116,
  atomRadius: 16,
  atomTextGap: 18,
  atomFont: 48,
  /** The foot: the claim, then the mark. */
  quoteFont: 60,
  quoteFontMin: 38,
  quoteLine: 1.18,
  quoteBelow: 34,
  markFont: 30,
  markUrlFont: 36,
};

const CONTENT = CARD_SIZE - L.padX * 2;

/** Full-width CJK / kana / fullwidth-forms — one em per glyph, near enough. */
const WIDE = /[\u2E80-\u9FFF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFF60\uFFE0-\uFFE6]/;

/**
 * Width estimate for a run of text, used when no real measurer is supplied
 * (the pure-node tests). CJK is monospaced at 1em by construction; Latin
 * averages out near 0.52em across mixed case. Only ever used to CHOOSE a font
 * size, never to position a glyph, so an estimate is honest here.
 * @param {string} text
 * @param {number} px
 * @returns {number}
 */
export function estimateTextWidth(text, px) {
  let w = 0;
  for (const ch of text) w += WIDE.test(ch) ? px : px * 0.52;
  return w;
}

/**
 * @typedef {{ zh: string, url?: string | undefined }} CardItem
 * @typedef {{
 *   sentences: CardItem[],
 *   atoms: CardItem[],
 *   builtFrom: string,
 *   lines: string[],
 *   markLead: string,
 *   markUrl: string,
 * }} CardModel
 * @typedef {{ url: string, x: number, y: number, w: number, h: number }} TileRect
 * @typedef {(text: string, px: number, weight: number) => number} Measurer
 */

/** XML-escape a run of text destined for an SVG `<text>` node. */
function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Largest size in [min, start] at which `text` fits `maxWidth`.
 *
 * This is not belt-and-braces. The claim line is the longest run on the card
 * and the font under it is WHOEVER'S SYSTEM IS RENDERING — PingFang on Apple,
 * Microsoft YaHei on Windows, Noto on Android — and those do not agree on Latin
 * advance widths. A size hardcoded to look right on the machine that built it
 * overflows the card on somebody else's, silently, in the one artifact whose
 * whole job is to be seen by other people.
 * @param {string} text
 * @param {number} maxWidth
 * @param {number} start
 * @param {number} min
 * @param {Measurer} measure
 * @param {number} weight
 * @returns {number}
 */
function fitFont(text, maxWidth, start, min, measure, weight) {
  let px = start;
  while (px > min && measure(text, px, weight) > maxWidth) px -= 1;
  return px;
}

/**
 * Baseline for a line of text whose glyph box is vertically centred on `cy`.
 * CJK glyphs sit roughly from −0.88em to +0.12em of the baseline, so their
 * visual centre is about 0.38em above it.
 */
const centreBaseline = (cy, px) => cy + px * 0.36;

/** Baseline for a line of text whose glyph box STARTS at `top`. */
const topBaseline = (top, px) => top + px * 0.88;

/**
 * Build the card: the SVG markup, and the rects the six drawings go into.
 *
 * PURE — no DOM, no canvas, no network. That is what lets the card's content
 * rules (no count, no time claim, all six phrases present, the claim line
 * intact) be pinned by a test that reads the markup, rather than by looking at
 * a picture.
 *
 * @param {CardModel} model
 * @param {Measurer} [measure]  real text measurement, when a canvas is at hand
 * @returns {{ svg: string, tiles: TileRect[] }}
 */
export function buildCard(model, measure) {
  const m = measure ?? ((text, px) => estimateTextWidth(text, px));
  /** @type {TileRect[]} */
  const tiles = [];
  /** @type {string[]} */
  const body = [];

  const cellRect = (x, y, w, h, r) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${CARD_COLORS.cell}"/>`;

  /** Reserve a white cell AND the drawing that goes in it, in one place. */
  const cell = (item, x, y, size, radius) => {
    body.push(cellRect(x, y, size, size, radius));
    if (item.url) tiles.push({ url: item.url, x, y, w: size, h: size });
  };

  // ── The two sentences ───────────────────────────────────────────────────
  let y = L.padTop;
  for (const s of model.sentences) {
    cell(s, L.padX, y, L.sentCell, L.sentRadius);
    const tx = L.padX + L.sentCell + L.sentGap;
    body.push(
      `<text class="sent" x="${tx}" y="${centreBaseline(y + L.sentCell / 2, L.sentFont)}">${esc(s.zh)}</text>`,
    );
    y += L.sentCell + L.sentRowGap;
  }
  y -= L.sentRowGap;

  // ── The hairline, and what the sentences were built from ────────────────
  y += L.ruleAbove;
  body.push(
    `<rect x="${L.padX}" y="${y}" width="${CONTENT}" height="1" fill="${CARD_COLORS.rule}"/>`,
  );
  y += L.ruleBelow;
  body.push(
    `<text class="label" x="${L.padX}" y="${y + L.labelFont}">${esc(model.builtFrom.toUpperCase())}</text>`,
  );
  y += L.labelFont + L.labelBelow;

  // ── The four atoms ──────────────────────────────────────────────────────
  const cols = Math.max(model.atoms.length, 1);
  const colW = (CONTENT - L.atomGap * (cols - 1)) / cols;
  model.atoms.forEach((a, i) => {
    const x = L.padX + i * (colW + L.atomGap);
    cell(a, x, y, L.atomCell, L.atomRadius);
    const top = y + L.atomCell + L.atomTextGap;
    body.push(`<text class="atom" x="${x}" y="${topBaseline(top, L.atomFont)}">${esc(a.zh)}</text>`);
  });

  // ── The foot: the claim, then the mark ──────────────────────────────────
  // Pinned UP from the bottom edge, not down from the atoms: the claim and the
  // mark are the two things a reader's eye lands on last, and their distance
  // from the card's edge is what makes the card look composed rather than
  // stopped. Slack, if the atoms end early, becomes air in the middle.
  const markBaseline = CARD_SIZE - L.padBottom;
  const markX = CARD_SIZE - L.padX;
  body.push(
    `<text class="mark" x="${markX}" y="${markBaseline}" text-anchor="end">` +
      `<tspan class="mark-lead">${esc(model.markLead)}</tspan>` +
      `<tspan class="mark-url" dx="16">${esc(model.markUrl)}</tspan></text>`,
  );

  const longest = model.lines.reduce((a, b) => (a.length >= b.length ? a : b), "");
  const quotePx = fitFont(longest, CONTENT, L.quoteFont, L.quoteFontMin, m, 400);
  const lineStep = Math.round(quotePx * L.quoteLine);
  const lastQuoteBaseline = markBaseline - L.markUrlFont - L.quoteBelow;
  model.lines.forEach((line, i) => {
    const baseline = lastQuoteBaseline - (model.lines.length - 1 - i) * lineStep;
    body.push(
      `<text class="quote" x="${L.padX}" y="${baseline}" font-size="${quotePx}">${esc(line)}</text>`,
    );
  });

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_SIZE}" height="${CARD_SIZE}" ` +
    `viewBox="0 0 ${CARD_SIZE} ${CARD_SIZE}">` +
    `<style>text{font-family:${CARD_FONT};fill:${CARD_COLORS.fg};}` +
    `.sent{font-size:${L.sentFont}px;font-weight:600;letter-spacing:0.02em;}` +
    `.label{font-size:${L.labelFont}px;letter-spacing:0.12em;fill:${CARD_COLORS.muted};}` +
    `.atom{font-size:${L.atomFont}px;}` +
    `.mark{font-size:${L.markFont}px;}` +
    `.mark-lead{fill:${CARD_COLORS.muted};}` +
    `.mark-url{font-size:${L.markUrlFont}px;font-weight:600;}` +
    `</style>` +
    `<rect width="${CARD_SIZE}" height="${CARD_SIZE}" fill="${CARD_COLORS.bg}"/>` +
    body.join("") +
    `</svg>`;

  return { svg, tiles };
}

/**
 * The SVG as a `data:` URL an `Image` will load.
 *
 * `data:` rather than a `blob:` URL on purpose: a blob URL for an SVG image has
 * a long history of engine-specific refusals, and this document is a few KB of
 * text — the size argument for blob URLs never applies. `encodeURIComponent`
 * carries the Chinese through intact under the declared UTF-8 charset.
 * @param {string} svg
 * @returns {string}
 */
export function svgDataUrl(svg) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * The card's own cache key for a drawing.
 *
 * MEASURED, NOT DEFENSIVE (Chrome 2026-09-10, against the live CDN). The reveal
 * has already loaded every one of these six through `preloadImage()` — a plain,
 * NON-CORS `Image` — and the browser's image cache does not key on the request's
 * CORS mode. So the card's `crossorigin="anonymous"` request is answered from
 * that cached opaque response, the CORS check it never made fails, and the load
 * ERRORS. Not a taint, not an exception: six empty white cells on an otherwise
 * perfect card, with nothing in the console.
 *
 *   img (no crossorigin)                 -> loads, 594px wide
 *   img crossorigin, same URL            -> ERROR      <- the bug
 *   img crossorigin, URL + a query param -> loads, 594px wide
 *
 * A constant query param is the whole fix: it is a different cache entry, so
 * the CORS request is actually made and actually answered (`ACAO: *`), and
 * being CONSTANT it is still cached for a repeat visitor and a repeat share.
 * It costs one extra fetch per drawing, once — about 80 KB across the six.
 * @param {string} url
 * @returns {string}
 */
function corsUrl(url) {
  return url + (url.includes("?") ? "&" : "?") + "cors=1";
}

/**
 * Load one image. `cors` sets `crossorigin="anonymous"` BEFORE `src`, which is
 * the whole taint story (see the header): set after, the attribute is ignored
 * and the canvas is poisoned at `toBlob()` time, far from the cause. It also
 * routes through `corsUrl()`, which is what makes the request reach the network
 * at all rather than a non-CORS cache hit.
 * @param {string} url
 * @param {boolean} cors
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url, cors) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image failed: ${url}`));
    img.src = cors ? corsUrl(url) : url;
  });
}

/**
 * Draw `img` into `rect` the way the app's image cells do — `object-fit:
 * contain` at 94%, so a wide drawing and a tall one both sit centred with the
 * same optical margin.
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement} img
 * @param {TileRect} rect
 */
function drawContain(ctx, img, rect) {
  const boxW = rect.w * 0.94;
  const boxH = rect.h * 0.94;
  const nw = img.naturalWidth || boxW;
  const nh = img.naturalHeight || boxH;
  const scale = Math.min(boxW / nw, boxH / nh);
  const w = nw * scale;
  const h = nh * scale;
  ctx.drawImage(img, rect.x + (rect.w - w) / 2, rect.y + (rect.h - h) / 2, w, h);
}

/**
 * Build the card and hand back the finished PNG.
 *
 * ── WHY EVERYTHING HAPPENS HERE, INCLUDING THE ENCODE ──────────────────────
 *
 * `navigator.share()` has to be reached from inside the Share click on iOS
 * (Brief 021 Gotcha 3), so the question is what the click still has to wait for.
 * Measured in Chrome against the live CDN, on an already-decoded card:
 *
 *   rasterise the SVG onto the canvas      1 ms
 *   composite the six drawings             5 ms
 *   canvas.toBlob(..., "image/png")     ~1050 ms   <- all of it
 *
 * The encode is the whole cost, and it is not something a click should carry —
 * so the caller starts this when the REVEAL MOUNTS, while the learner is
 * reading, and the tap then has a `Blob` already in hand and no `await` at all
 * between the gesture and the share sheet. (`toBlob` is asynchronous because
 * the encoder runs off the main thread, so that second is background work, not
 * a frozen reveal.)
 *
 * This is why the module says nothing about `record_generate`: the event is the
 * screen's, and it marks the first record a learner actually ASKED for, not
 * every card this speculatively encodes. The two are deliberately not the same
 * moment — see `_generateCard` in l2b-exercise.js.
 *
 * A tile that fails to load is skipped, not fatal — five drawings and one empty
 * cell is a card; an exception is not.
 * @param {CardModel} model
 * @returns {Promise<Blob>}
 */
export async function prepareCard(model) {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_SIZE;
  canvas.height = CARD_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("record card: no 2d context");

  /** @type {Measurer} */
  const measure = (text, px, weight) => {
    ctx.font = `${weight} ${px}px ${CARD_FONT}`;
    return ctx.measureText(text).width;
  };

  const { svg, tiles } = buildCard(model, measure);
  const [svgImg, drawings] = await Promise.all([
    loadImage(svgDataUrl(svg), false),
    Promise.all(tiles.map((t) => loadImage(t.url, true).catch(() => null))),
  ]);

  ctx.drawImage(svgImg, 0, 0, CARD_SIZE, CARD_SIZE);
  drawings.forEach((img, i) => {
    if (img) drawContain(ctx, img, tiles[i]);
  });

  return await new Promise((resolve, reject) => {
    // Throws SecurityError on a tainted canvas. It should not — every
    // cross-origin pixel here arrived through a CORS request against a CDN that
    // answers `ACAO: *`, on its own cache key — but this is where that would
    // surface, so it is a rejection the caller can act on rather than an
    // uncaught throw.
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("record card: toBlob produced nothing"));
      }, "image/png");
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
